import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const CompanyPerformanceChart = () => {
  const theme = useTheme();
  const [metricType, setMetricType] = useState("minutesUsed");
  const [timeRange, setTimeRange] = useState("last7Days");

  // Sample data for the chart
  const data = [
    {
      name: "Jan",
      "Acme Corporation": 6500,
      "TechTron Inc.": 2800,
      "Widget Industries": 5900,
      "StarCall Solutions": 2200,
      "Global Services": 4200,
    },
    {
      name: "Feb",
      "Acme Corporation": 7800,
      "TechTron Inc.": 3400,
      "Widget Industries": 6800,
      "StarCall Solutions": 2800,
      "Global Services": 5100,
    },
    {
      name: "Mar",
      "Acme Corporation": 9800,
      "TechTron Inc.": 4200,
      "Widget Industries": 7900,
      "StarCall Solutions": 3500,
      "Global Services": 6300,
    },
    {
      name: "Apr",
      "Acme Corporation": 12500,
      "TechTron Inc.": 5400,
      "Widget Industries": 9200,
      "StarCall Solutions": 4200,
      "Global Services": 7200,
    },
    {
      name: "May",
      "Acme Corporation": 14800,
      "TechTron Inc.": 6800,
      "Widget Industries": 10500,
      "StarCall Solutions": 5100,
      "Global Services": 8400,
    },
    {
      name: "Jun",
      "Acme Corporation": 17200,
      "TechTron Inc.": 8200,
      "Widget Industries": 12800,
      "StarCall Solutions": 6300,
      "Global Services": 10200,
    },
    {
      name: "Jul",
      "Acme Corporation": 19500,
      "TechTron Inc.": 9200,
      "Widget Industries": 13900,
      "StarCall Solutions": 7200,
      "Global Services": 11500,
    },
  ];

  // Company colors
  const companyColors = {
    "Acme Corporation": "#4285F4", // Blue
    "TechTron Inc.": "#EA4335", // Red
    "Widget Industries": "#A142F4", // Purple
    "StarCall Solutions": "#FBBC05", // Yellow/Orange
    "Global Services": "#0F9D58", // Green
  };

  const handleMetricChange = (event) => {
    setMetricType(event.target.value);
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const formatYAxis = (value) => {
    if (value >= 1000) {
      return `${value / 1000}K`;
    }
    return value;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ borderRadius: 2, border: "1px solid #ebedf0", p: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="body1" component="div" fontWeight="500">
                Company Performance Metrics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compare key metrics across companies
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Metric Autocomplete */}
              <Autocomplete
                options={[
                  { label: "Minutes Used", value: "minutesUsed" },
                  { label: "Calls Processed", value: "callsProcessed" },
                  { label: "Active Users", value: "activeUsers" },
                ]}
                size="small"
                value={
                  [
                    { label: "Minutes Used", value: "minutesUsed" },
                    { label: "Calls Processed", value: "callsProcessed" },
                    { label: "Active Users", value: "activeUsers" },
                  ].find((option) => option.value === metricType) || null
                }
                onChange={(e, newValue) => {
                  setMetricType(newValue?.value || "");
                }}
                getOptionLabel={(option) => option?.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Metric"
                    // size="small"
                    sx={{ minWidth: 180 }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        height: 35,
                        "& input": {
                          height: 35,
                          padding: "0 14px",
                        },
                      },
                    }}
                  />
                )}
              />

              {/* Time Range Autocomplete */}
              <Autocomplete
                options={[
                  { label: "Last 7 Days", value: "last7Days" },
                  { label: "Last 30 Days", value: "last30Days" },
                  { label: "Last 3 Months", value: "last3Months" },
                  { label: "Last Year", value: "lastYear" },
                ]}
                size="small"
                value={
                  [
                    { label: "Last 7 Days", value: "last7Days" },
                    { label: "Last 30 Days", value: "last30Days" },
                    { label: "Last 3 Months", value: "last3Months" },
                    { label: "Last Year", value: "lastYear" },
                  ].find((option) => option.value === timeRange) || null
                }
                onChange={(e, newValue) => {
                  setTimeRange(newValue?.value || "");
                }}
                getOptionLabel={(option) => option?.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value?.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Time Range"
                    // size="small"
                    sx={{ minWidth: 180 }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        height: 35,
                        "& input": {
                          height: 35,
                          padding: "0 14px",
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              height: 400,
              width: "100%",
              mb: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <ResponsiveContainer width="100%" height="60%">
              <LineChart
                data={data}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fill: "#666" }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fill: "#666" }} />
                <Tooltip />
                <Legend />
                {Object.keys(companyColors).map((company) => (
                  <Line
                    key={company}
                    type="monotone"
                    dataKey={company}
                    stroke={companyColors[company]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Minutes used per month by company (Jan - Jul 2023)
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Source: Speech Analytics Platform Usage Logs
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyPerformanceChart;
